import { useEffect, useRef } from 'react'
import { useTranslation } from '../../i18n'
import { uiLocale } from '../../lib/dateFormat'
import { formatReplayDuration, formatSkipStep } from '../../lib/replayPlayback'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'
import { REPLAY_CHROME } from './replayChrome'

/** How long a press has to be held before it opens the step chooser instead. */
const LONG_PRESS_MS = 500

/** The filled portion of the scrubber track. */
const TRACK_FILL = '#7ea36b'

export interface ReplayPlaybackBarProps {
  startsAt: number
  endsAt: number
  cursor: number
  isPlaying: boolean
  speed: number
  skipStepMs: number
  onToggle: () => void
  onSeek: (cursor: number) => void
  onSkip: (direction: 1 | -1) => void
  onOpenSpeedChooser: () => void
  onOpenSkipChooser: () => void
  /** The two chooser popovers, anchored to their own chips. */
  speedChooser?: React.ReactNode
  skipChooser?: React.ReactNode
  speedChooserOpen?: boolean
  skipChooserOpen?: boolean
}

/**
 * The bottom control bar: play, scrubber, then the skip chips flanking the clock,
 * then the two chooser chips.
 *
 * The skip controls sit either side of the time readout on purpose — skip back,
 * where you are, skip forward reads as one unit, which is how it is operated.
 *
 * The scrubber is a native range input. It comes with keyboard operation, drag, and
 * a screen-reader contract for free, none of which a hand-rolled track would have
 * without being rebuilt from scratch.
 */
export function ReplayPlaybackBar({
  startsAt,
  endsAt,
  cursor,
  isPlaying,
  speed,
  skipStepMs,
  onToggle,
  onSeek,
  onSkip,
  onOpenSpeedChooser,
  onOpenSkipChooser,
  speedChooser,
  skipChooser,
  speedChooserOpen = false,
  skipChooserOpen = false,
}: ReplayPlaybackBarProps) {
  const { t, i18n } = useTranslation('replay')
  const locale = uiLocale(i18n.language)

  const total = endsAt - startsAt
  const elapsed = cursor - startsAt
  const stepLabel = formatSkipStep(skipStepMs)
  const stepName = skipStepName(skipStepMs, t)
  const progress = total > 0 ? (elapsed / total) * 100 : 0

  // Wall-clock time of day at the cursor, and the hunt's own length beside it — the
  // two numbers a judge reads off this bar.
  const timeOfDay = new Date(cursor).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const huntDate = new Date(startsAt).toLocaleDateString(locale, { day: 'numeric', month: 'short' })

  return (
    <div
      data-testid="replay-playback-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        padding: '10px 16px',
        background: REPLAY_CHROME.barBackground,
        borderTop: `1px solid ${REPLAY_CHROME.hairline}`,
        flexShrink: 0,
      }}
    >
      <button
        type="button"
        aria-label={isPlaying ? t('controls.pause') : t('controls.play')}
        onClick={onToggle}
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: 'none',
          background: COLORS.primary,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <Icon name={isPlaying ? 'pause' : 'play'} size={22} filled />
      </button>

      <input
        type="range"
        aria-label={t('controls.scrubber')}
        className="wh-replay-range"
        min={startsAt}
        max={endsAt}
        step={1000}
        value={cursor}
        onChange={(e) => onSeek(Number(e.target.value))}
        style={{
          flex: '1 1 180px',
          minWidth: 140,
          background: `linear-gradient(to right, ${TRACK_FILL} ${progress}%, ${REPLAY_CHROME.trackBackground} ${progress}%)`,
        }}
      />

      <SkipChip
        label={t('controls.skipBack', { step: stepName })}
        step={stepLabel}
        onClick={() => onSkip(-1)}
        onLongPress={onOpenSkipChooser}
      />

      {/* Tabular figures so the readout does not jitter as the digits change. */}
      <span
        data-testid="replay-clock"
        style={{
          fontSize: 13,
          color: '#fff',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {huntDate} {timeOfDay} / {formatReplayDuration(total)}
      </span>

      <SkipChip
        forward
        label={t('controls.skipForward', { step: stepName })}
        step={stepLabel}
        onClick={() => onSkip(1)}
        onLongPress={onOpenSkipChooser}
      />

      <ChooserChip
        label={t('controls.skipBy')}
        text={t('skip.chip', { step: stepName })}
        open={skipChooserOpen}
        onClick={onOpenSkipChooser}
      >
        {skipChooser}
      </ChooserChip>

      <ChooserChip
        label={t('controls.speed')}
        text={t('speed.value', { value: speed })}
        open={speedChooserOpen}
        onClick={onOpenSpeedChooser}
      >
        {speedChooser}
      </ChooserChip>
    </div>
  )
}

/** The step spoken in full — "15 sec", "1 min" — for labels and the chooser chip. */
export function skipStepName(ms: number, t: (key: string, options?: Record<string, unknown>) => string): string {
  return ms < 60_000
    ? `${Math.round(ms / 1000)} ${t('units.sec')}`
    : `${Math.round(ms / 60_000)} ${t('units.min')}`
}

/**
 * A skip button: the step value beside the replay glyph, mirrored for forward.
 *
 * Long-press opens the step chooser, matching the mobile gesture; the chooser is
 * also reachable from the visible "Skip by" chip, so the gesture is a shortcut
 * rather than the only way in.
 */
function SkipChip({
  label,
  step,
  forward,
  onClick,
  onLongPress,
}: {
  label: string
  step: string
  forward?: boolean
  onClick: () => void
  onLongPress: () => void
}) {
  // Refs, not state: the click handler has to read whether the press already became
  // a long press, and a state update is not guaranteed to have been applied by the
  // time the click that follows pointerup runs.
  const timerRef = useRef<number | null>(null)
  const longPressedRef = useRef(false)

  const start = () => {
    longPressedRef.current = false
    timerRef.current = window.setTimeout(() => {
      longPressedRef.current = true
      onLongPress()
    }, LONG_PRESS_MS)
  }
  const end = () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    timerRef.current = null
  }

  // A press interrupted by an unmount would otherwise open the chooser on a
  // component that is already gone.
  useEffect(() => end, [])

  return (
    <button
      type="button"
      aria-label={label}
      onPointerDown={start}
      onPointerUp={end}
      onPointerLeave={end}
      // A long press already opened the chooser; skipping as well would move the
      // cursor the viewer was about to reconfigure.
      onClick={() => {
        if (!longPressedRef.current) onClick()
      }}
      style={{ ...REPLAY_CHROME.chip, gap: 5 }}
    >
      {step}
      <span
        aria-hidden="true"
        style={{ display: 'inline-flex', transform: forward ? 'scaleX(-1)' : undefined }}
      >
        <Icon name="replay" size={16} />
      </span>
    </button>
  )
}

/** A chip that opens a chooser popover anchored above it. */
function ChooserChip({
  label,
  text,
  open,
  onClick,
  children,
}: {
  label: string
  text: string
  open: boolean
  onClick: () => void
  children?: React.ReactNode
}) {
  return (
    <span style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={onClick}
        style={{
          ...REPLAY_CHROME.chip,
          background: open ? 'rgba(255,255,255,0.25)' : REPLAY_CHROME.chip.background,
        }}
      >
        {text}
      </button>
      {children}
    </span>
  )
}
